
class AjaxCart extends HTMLElement {
  constructor() {
    super();

    this.cartForm = this.querySelector('form[data-cart-form]');
    this.cartDrawer = document.querySelector('#component-cart-drawer');

    // Cart operation lock and queue
    this.isCartUpdating = false;
    this.cartOperationQueue = [];
    this.currentCartOperation = null;
    
    // Flag to track if drawer has been opened before (for first-time detection)
    this.hasDrawerOpenedBefore = false;

    this.updateEvents();

    this.cartForm.addEventListener('keydown', (e)=>{
      if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
      }
    });
    if (navigator.platform === 'iPhone') document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }

  /**
   * Observe attribute of component
   * 
   * @returns {array} Attributes to Observe
   */
  static get observedAttributes() {
    return ['updating'];
  }

  /**
   * Called when element is inserted into DOM
   */
  connectedCallback() {
    
    // Re-initialize drawer reference if not set (useful after login/page reload)
    if (!this.cartDrawer) {
      this.cartDrawer = document.querySelector('#component-cart-drawer');
    }
    
    // Ensure drawer is ready after connection
    if (this.cartDrawer && typeof this.cartDrawer.openDrawer !== 'function') {
      // Wait a bit for custom element to upgrade
      setTimeout(() => {
        this.cartDrawer = document.querySelector('#component-cart-drawer');
      }, 100);
    }
    
    // Check if page needs to be reloaded (first visit or logged in customer)
    this.checkAndReloadIfNeeded();
  }

  /**
   * Check if page needs to be reloaded and reload if needed
   * Reloads on first visit or if customer is logged in (but only once per session)
   */
  checkAndReloadIfNeeded() {
    
    // Skip reload check if we're on cart page
    if (window.globalSpace && window.globalSpace.template === 'cart') {
      return;
    }

    // Skip if already reloaded in this session (prevent infinite reload loop)
    const reloadedKey = 'cart_drawer_page_reloaded';
    const alreadyReloaded = sessionStorage.getItem(reloadedKey);
    if (alreadyReloaded) {
      return;
    }

    // Skip if drawer has been successfully opened before (indicates everything is working)
    const drawerOpenedKey = 'cart_drawer_opened_successfully';
    const drawerOpened = sessionStorage.getItem(drawerOpenedKey);
    if (drawerOpened) {
      return;
    }

    // Check if this is the first visit using sessionStorage
    const firstVisitKey = 'cart_drawer_first_visit';
    const hasVisitedBefore = sessionStorage.getItem(firstVisitKey);
    
    // Check if customer is logged in from globalSpace
    const isCustomerLoggedIn = window.globalSpace && window.globalSpace.customer_logged_in === true;
    
    // Reload if: first visit OR customer is logged in (but only if drawer hasn't been opened)
    const shouldReload = (!hasVisitedBefore || isCustomerLoggedIn);
    
    if (shouldReload) {
      // Mark that we've visited (for first visit check)
      sessionStorage.setItem(firstVisitKey, 'true');
      
      // Mark that we've reloaded to prevent infinite loop
      sessionStorage.setItem(reloadedKey, 'true');
      
      // Reload the page
      window.location.reload();
    }
  }

  /**
   * Robust drawer opening method - uses extensive waiting and fallbacks
   * This is used when drawer doesn't open on first add-to-cart attempt
   */
  openDrawerRobust(elementTofocus) {
    // Only open if cart drawer is enabled
    if (!window.globalSpace || !window.globalSpace.cart_drawer) {
      return;
    }

    // Check if we're on cart page (don't open drawer on cart page)
    if (window.globalSpace.template === 'cart') {
      return;
    }

    // Wait for DOM to be fully ready
    const tryOpenDrawer = () => {
      // Use customElements.whenDefined for reliable waiting
      const waitForDrawerReady = async () => {
        const drawerElement = document.querySelector('#component-cart-drawer');
        
        if (!drawerElement) {
          return null;
        }

        // Use customElements.whenDefined if available (most reliable)
        if (typeof customElements !== 'undefined' && customElements.whenDefined) {
          try {
            // Wait for custom-drawer to be defined
            await customElements.whenDefined('custom-drawer');
            
            // Check if drawer is upgraded (has openDrawer method)
            if (typeof drawerElement.openDrawer === 'function') {
              return drawerElement;
            }
            
            // If not upgraded yet, wait a bit more (up to 2 seconds)
            const maxWait = 2000;
            const startTime = Date.now();
            
            while ((Date.now() - startTime) < maxWait) {
              if (typeof drawerElement.openDrawer === 'function') {
                return drawerElement;
              }
              await new Promise(resolve => setTimeout(resolve, 50));
            }
            
            // Final check
            return (typeof drawerElement.openDrawer === 'function') ? drawerElement : drawerElement;
          } catch (error) {
            return drawerElement; // Return element even if not upgraded, we'll force-open
          }
        }
        
        // Fallback: polling method if whenDefined not available
        return new Promise((resolve) => {
          let attempts = 0;
          const maxAttempts = 40;
          const checkInterval = 50;
          
          const checkDrawer = () => {
            attempts++;
            
            if (typeof customElements !== 'undefined' && customElements.get('custom-drawer')) {
              if (drawerElement && typeof drawerElement.openDrawer === 'function') {
                resolve(drawerElement);
                return;
              }
            }
            
            if (attempts < maxAttempts) {
              setTimeout(checkDrawer, checkInterval);
            } else {
              // Return element even if not upgraded - we'll force-open
              resolve(drawerElement);
            }
          };
          
          checkDrawer();
        });
      };
      
      // Wait for drawer to be ready, then open it
      waitForDrawerReady().then((drawer) => {
        if (!drawer) {
          return;
        }

        // Try normal openDrawer method first
        if (typeof drawer.openDrawer === 'function') {
          this.cartDrawer = drawer;
          try {
            // Use provided elementTofocus or find drawer toggle button
            const buttonToFocus = elementTofocus || document.querySelector('[data-toggleDrawer="component-cart-drawer"]') || null;
            drawer.openDrawer(buttonToFocus);
            this.hasDrawerOpenedBefore = true;
            sessionStorage.setItem('cart_drawer_opened_successfully', 'true');
            return; // Success!
          } catch (error) {
            // Fall through to force-open
          }
        }
        
        // If openDrawer method not available or failed, force-open
        const forceResult = this.forceOpenDrawer(elementTofocus);
        if (forceResult) {
          this.hasDrawerOpenedBefore = true;
          sessionStorage.setItem('cart_drawer_opened_successfully', 'true');
        } else {
          // Enhanced fallback: try multiple times with increasing delays
          const fallbackAttempts = [
            { delay: 500, message: 'First fallback attempt (robust)' },
            { delay: 1000, message: 'Second fallback attempt (robust)' },
            { delay: 2000, message: 'Final fallback attempt (robust)' }
          ];
          
          fallbackAttempts.forEach((attempt) => {
            setTimeout(() => {
              const drawer = document.querySelector('#component-cart-drawer');
              if (drawer) {
                if (typeof drawer.openDrawer === 'function') {
                  try {
                    const buttonToFocus = elementTofocus || document.querySelector('[data-toggleDrawer="component-cart-drawer"]') || null;
                    drawer.openDrawer(buttonToFocus);
                    this.hasDrawerOpenedBefore = true;
                    sessionStorage.setItem('cart_drawer_opened_successfully', 'true');
                  } catch (e) {
                    if (this.forceOpenDrawer(elementTofocus)) {
                      this.hasDrawerOpenedBefore = true;
                      sessionStorage.setItem('cart_drawer_opened_successfully', 'true');
                    }
                  }
                } else {
                  if (this.forceOpenDrawer(elementTofocus)) {
                    this.hasDrawerOpenedBefore = true;
                    sessionStorage.setItem('cart_drawer_opened_successfully', 'true');
                  }
                }
              }
            }, attempt.delay);
          });
        }
      }).catch((error) => {
        console.error('Failed to open cart drawer with robust method:', error);
        // Last resort: force-open
        if (this.forceOpenDrawer(elementTofocus)) {
          this.hasDrawerOpenedBefore = true;
        }
      });
    };

    // Execute immediately (no need to wait for DOMContentLoaded since this is called from initCartDrawer)
    tryOpenDrawer();
  }

  /**
   * Execute cart operation with queue management
   * @param {Function} operation - Async function that performs the cart operation
   * @returns {Promise} - Promise that resolves when operation completes
   */
  async executeCartOperation(operation) {
    return new Promise((resolve, reject) => {
      const queueItem = { operation, resolve, reject };
      
      if (this.isCartUpdating) {
        // Queue the operation
        this.cartOperationQueue.push(queueItem);
        return;
      }

      // Execute immediately
      this.processCartOperation(queueItem);
    });
  }

  /**
   * Process cart operation from queue
   * @param {Object} queueItem - Queue item with operation, resolve, reject
   */
  async processCartOperation(queueItem) {
    this.isCartUpdating = true;
    this.currentCartOperation = queueItem;

    try {
      const result = await queueItem.operation();
      queueItem.resolve(result);
    } catch (error) {
      console.error('[CART-DRAWER] processCartOperation - Error caught:', error);
      console.error('[CART-DRAWER] processCartOperation - Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // Show user-friendly error notification
      if (window.notificationEle) {
        const errorMessage = error.message || 'An error occurred while processing your cart. Please try again.';
        window.notificationEle.updateNotification('Cart Operation Failed', errorMessage, {
          type: 'error',
          timeout: 5000
        });
      }
      
      queueItem.reject(error);
    } finally {
      this.isCartUpdating = false;
      this.currentCartOperation = null;

      // Process next item in queue
      if (this.cartOperationQueue.length > 0) {
        const nextItem = this.cartOperationQueue.shift();
        this.processCartOperation(nextItem);
      }
    }
  }

  /**
   * bind Other inside element events to DOM
   * Fixed: Remove old listeners before adding new ones
   */
  updateEvents(){
    // Store bound handlers for cleanup
    if (!this._boundHandlers) {
      this._boundHandlers = {
        removeItem: this.removeItem.bind(this),
        qtyChange: this.onQtyChange.bind(this)
      };
    }

    // Remove existing listeners before adding new ones
    this.cartForm.querySelectorAll('[data-item-remove]').forEach(button => {
      button.removeEventListener('click', this._boundHandlers.removeItem);
      button.addEventListener('click', this._boundHandlers.removeItem);
    });
    
    this.cartForm.querySelectorAll('[data-qty-input]').forEach(input => {
      input.removeEventListener('change', this._boundHandlers.qtyChange);
      input.addEventListener('change', this._boundHandlers.qtyChange);
    });
  }

  /**
   * Force open drawer by manually manipulating DOM (last resort)
   */
  forceOpenDrawer(elementTofocus) {
    const drawer = document.querySelector('#component-cart-drawer');
    if (!drawer) {
      console.error('[CART-DRAWER] ❌ Drawer element not found in DOM');
      return false;
    }

    try {
      // Manually replicate openDrawer functionality
      drawer.classList.add('open');
      drawer.setAttribute('aria-hidden', 'false');
      
      // Show overlay if SiteOverlay exists
      if (typeof SiteOverlay !== 'undefined' && SiteOverlay.prototype && typeof SiteOverlay.prototype.showOverlay === 'function') {
        SiteOverlay.prototype.showOverlay();
      } else {
        // Fallback: show overlay manually
        const overlay = document.querySelector('.site-overlay') || document.querySelector('[data-overlay]');
        if (overlay) {
          overlay.style.display = 'block';
          overlay.style.opacity = '1';
          overlay.style.visibility = 'visible';
        }
      }
      
      // Set focus
      const focusElement = drawer.querySelector('.drawer-header') || drawer.querySelector('.first-focusable-ele') || drawer;
      if (focusElement && typeof Utility !== 'undefined' && Utility.forceFocus) {
        Utility.forceFocus(focusElement);
      } else {
        focusElement?.focus();
      }
      
      // Set openedBy attribute
      if (elementTofocus) {
        elementTofocus.setAttribute('aria-expanded', 'true');
      }
      
      return true;
    } catch (error) {
      console.error('[CART-DRAWER] ❌ Error force-opening drawer:', error);
      return false;
    }
  }

  /**
   * Open Cart drawer and add focus to drawer container
   */
  initCartDrawer(elementTofocus) {
    if(!window.globalSpace.cart_drawer){
      window.location.href = window.routes.cart_fetch_url || '/cart';
      return;
    }
    
    // Check if this is the first time trying to open drawer
    const isFirstAttempt = !this.hasDrawerOpenedBefore;
    
    // Check for company_location_changed query parameter (critical scenario)
    const urlParams = new URLSearchParams(window.location.search);
    const hasCompanyLocationChanged = urlParams.has('company_location_changed') && 
                                      urlParams.get('company_location_changed') === 'true';
    
    // Use customElements.whenDefined for reliable waiting (better than polling)
    const waitForDrawerReady = async () => {
      const drawerElement = document.querySelector('#component-cart-drawer');
      
      if (!drawerElement) {
        console.warn('[CART-DRAWER] ❌ Drawer element not found in DOM');
        return null;
      }

      // Use customElements.whenDefined if available (most reliable)
      if (typeof customElements !== 'undefined' && customElements.whenDefined) {
        try {
          // Wait for custom-drawer to be defined
          await customElements.whenDefined('custom-drawer');
          
          // Check if drawer is upgraded (has openDrawer method)
          const hasOpenDrawerMethod = typeof drawerElement.openDrawer === 'function';
          
          if (hasOpenDrawerMethod) {
            return drawerElement;
          }
          
          // If not upgraded yet, wait a bit more
          // Use longer wait for first attempt or company_location_changed
          const maxWait = (isFirstAttempt || hasCompanyLocationChanged) ? 2000 : 1000;
          const startTime = Date.now();
          
          while ((Date.now() - startTime) < maxWait) {
            if (typeof drawerElement.openDrawer === 'function') {
              return drawerElement;
            }
            await new Promise(resolve => setTimeout(resolve, 50));
          }
          
          // Final check
          const finalCheck = typeof drawerElement.openDrawer === 'function';
          return finalCheck ? drawerElement : drawerElement;
        } catch (error) {
          console.error('[CART-DRAWER] ❌ Error waiting for drawer custom element:', error);
          // Fallback to force-open
          return drawerElement; // Return element even if not upgraded, we'll force-open
        }
      }
      
      // Fallback: polling method if whenDefined not available
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = (isFirstAttempt || hasCompanyLocationChanged) ? 60 : 40;
        const checkInterval = 50;
        
        const checkDrawer = () => {
          attempts++;
          
          if (typeof customElements !== 'undefined' && customElements.get('custom-drawer')) {
            if (drawerElement && typeof drawerElement.openDrawer === 'function') {
              resolve(drawerElement);
              return;
            }
          }
          
          if (attempts < maxAttempts) {
            setTimeout(checkDrawer, checkInterval);
          } else {
            // Return element even if not upgraded - we'll force-open
            resolve(drawerElement);
          }
        };
        
        checkDrawer();
      });
    };
    
    // Wait for drawer to be ready, then open it
    waitForDrawerReady().then((drawer) => {
      if (!drawer) {
        // If first attempt failed, use robust method
        if (isFirstAttempt) {
          this.openDrawerRobust(elementTofocus);
          return;
        }
        if (window.notificationEle) {
          window.notificationEle.updateNotification('Cart', 'Unable to open cart drawer. Please refresh the page.', {
            type: 'warning',
            timeout: 5000
          });
        }
        return;
      }

      // Try normal openDrawer method first
      if (typeof drawer.openDrawer === 'function') {
        this.cartDrawer = drawer;
        try {
          drawer.openDrawer(elementTofocus);
          // Mark as successfully opened (both instance and sessionStorage)
          this.hasDrawerOpenedBefore = true;
          sessionStorage.setItem('cart_drawer_opened_successfully', 'true');
          return; // Success!
        } catch (error) {
          console.error('[CART-DRAWER] ❌ Error opening drawer:', error);
          // If first attempt failed, use robust method
          if (isFirstAttempt) {
            this.openDrawerRobust(elementTofocus);
            return;
          }
          // Fall through to force-open for subsequent attempts
        }
      }
      
      // If openDrawer method not available or failed
      // For first attempt, use robust method; otherwise use force-open
      if (isFirstAttempt) {
        this.openDrawerRobust(elementTofocus);
        return;
      }
      
      const forceOpenResult = this.forceOpenDrawer(elementTofocus);
      if (!forceOpenResult) {
        // Enhanced fallback: try multiple times with increasing delays
        const fallbackAttempts = [
          { delay: 500, message: 'First fallback attempt' },
          { delay: 1000, message: 'Second fallback attempt' },
          { delay: 2000, message: 'Final fallback attempt' }
        ];
        
        fallbackAttempts.forEach((attempt, index) => {
          setTimeout(() => {
            const drawer = document.querySelector('#component-cart-drawer');
            if (drawer) {
              if (typeof drawer.openDrawer === 'function') {
                try {
                  drawer.openDrawer(elementTofocus);
                  this.hasDrawerOpenedBefore = true;
                  sessionStorage.setItem('cart_drawer_opened_successfully', 'true');
                } catch (e) {
                  console.error('[CART-DRAWER] Fallback openDrawer() failed:', e);
                  const forceResult = this.forceOpenDrawer(elementTofocus);
                  if (forceResult) {
                    this.hasDrawerOpenedBefore = true;
                    sessionStorage.setItem('cart_drawer_opened_successfully', 'true');
                  }
                }
              } else {
                const forceResult = this.forceOpenDrawer(elementTofocus);
                if (forceResult) {
                  this.hasDrawerOpenedBefore = true;
                  sessionStorage.setItem('cart_drawer_opened_successfully', 'true');
                }
              }
            }
          }, attempt.delay);
        });
      } else {
        this.hasDrawerOpenedBefore = true;
        sessionStorage.setItem('cart_drawer_opened_successfully', 'true');
      }
    }).catch((error) => {
      console.error('[CART-DRAWER] ❌ Failed to open cart drawer:', error);
      // If first attempt failed, use robust method
      if (isFirstAttempt) {
        this.openDrawerRobust(elementTofocus);
        return;
      }
      // Last resort: force-open
      const forceResult = this.forceOpenDrawer(elementTofocus);
      if (forceResult) {
        this.hasDrawerOpenedBefore = true;
        sessionStorage.setItem('cart_drawer_opened_successfully', 'true');
      }
    });
  }

   /**
   * Fetch latest cart data 
   */
  //  getCartData(){
  //   const cartRoute = `${window.location.pathname}?sections=${this.dataset.sectionId}`;
  //   fetch(cartRoute).then(response => response.json()).then(response => {
  //       this._updateCart(response);
  //   }).catch((e) => {
  //       console.error(e);
  //   }).finally(() => {
  //       // Cart HTML fetch done
  //   });
  // }

  /**
   * Update cart HTML and Trigger Open Drawer event
   *
   * @param {string} cartHTML String formatted response from fetch cart.js call
   * @param {HTML Node} openDrawerBtn open drawer node
   */
  _updateCart(response, openDrawerBtn = null){
    if(response === null) {
      return;
    }
    
    
    // document.body.classList.remove('disable-interactions');
    this.setAttribute('updating', true);

    let cartHTML = response[this.dataset.sectionId] || response['template-cart'];
    const parser = new DOMParser();
    cartHTML = parser.parseFromString(cartHTML, 'text/html');

    const cartJSONEle = cartHTML.querySelector('[data-cartScriptJSON]');

    window.globalSpace.cart = JSON.parse(cartJSONEle.textContent);

    Utility.updateSourceFromDestination(`#cart-form-${this.dataset.sectionId}`, cartHTML);
    Utility.updateSourceFromDestination(`#cart-subtotal-${this.dataset.sectionId}`, cartHTML);
    // Utility.updateSourceFromDestination(`#cart-discount-${this.dataset.sectionId}`, cartHTML);
    Utility.updateSourceFromDestination(`#cart-total-${this.dataset.sectionId}`, cartHTML);
    // Utility.updateSourceFromDestination(`#cart-discount-codes-${this.dataset.sectionId}`, cartHTML);
    Utility.updateSourceFromDestination(`#cart-item-count-${this.dataset.sectionId}`, cartHTML);
    Utility.updateSourceFromDestination(`#cart-upsell-${this.dataset.sectionId}`, cartHTML);

    document.querySelector(`#cart-icon-desktop .cart-count`).innerHTML = window.globalSpace.cart.item_count;
    document.querySelector(`#cart-icon-mobile .cart-count`).innerHTML = window.globalSpace.cart.item_count;

    const cartFormContainer = this.querySelector(`#cart-form-container-${this.dataset.sectionId}`);
    (window.globalSpace.cart.item_count <= 0) ? cartFormContainer.classList.add('empty_cart') : cartFormContainer.classList.remove('empty_cart');

    (window.globalSpace.cart.item_count <= 0) ? document.querySelector(`#cart-icon-desktop .cart-count`).classList.add("d-none") : document.querySelector(`#cart-icon-desktop .cart-count`).classList.remove("d-none");
    (window.globalSpace.cart.item_count <= 0) ? document.querySelector(`#cart-icon-mobile .cart-count`).classList.add("d-none") : document.querySelector(`#cart-icon-mobile .cart-count`).classList.remove("d-none");

    if(document.querySelector(".cart-drawer-footer") != null){
      const cartFormFooter = document.querySelector(".cart-drawer-footer");
      (window.globalSpace.cart.item_count <= 0) ? cartFormFooter.classList.add('d-none') : cartFormFooter.classList.remove('d-none');
    }

    if(document.querySelector(".cart-upsell-update") != null){
      const cartUpsell = document.querySelector(".cart-upsell-update");
      (window.globalSpace.cart.item_count <= 0) ? cartUpsell.classList.add('d-none') : cartUpsell.classList.remove('d-none');
      lazyImageObserver.observe();
    }
    
    this.updateEvents();
    // window?.Shopify?.PaymentButton?.init();
    
    // CRITICAL: Ensure drawer opening happens after DOM updates and custom elements are ready
    // Use requestAnimationFrame to ensure DOM updates are complete, then setTimeout for custom element readiness
    
    if(window.globalSpace.cart_drawer && openDrawerBtn){
      // Use requestAnimationFrame to ensure DOM updates from updateSourceFromDestination are complete
      requestAnimationFrame(() => {
        // Use setTimeout to ensure custom elements have time to upgrade after DOM updates
        setTimeout(() => {
          this.initCartDrawer(openDrawerBtn);
        }, 100); // Small delay to ensure custom elements are ready
      });
    }
    
    this.removeAttribute('updating');
  } 
  
   /**
    * Update Quantity API call 
    *
    * @param {string} line Line Index value of cart Item (Starts from 1)
    * @param {integer} quantity Quantity to update
    */
  updateItemQty(line, quantity){
    // Prevent multiple simultaneous calls
    if (this.isCartUpdating) {
      return;
    }

    const lineItem = document.querySelectorAll('[data-cart-item]')[line-1];
    const qtyInput = this.cartForm.querySelector(`input[data-qty-input][data-index="${line}"]`);
    
    if(lineItem){ lineItem.classList.add('updating'); }
    
    // Disable inputs during operation
    if (qtyInput) {
      qtyInput.disabled = true;
    }
    this.cartForm.querySelectorAll('[data-item-remove]').forEach(btn => btn.disabled = true);

    const body = JSON.stringify({
      line: parseInt(line),
      quantity: parseInt(quantity),
      sections: `${this.dataset.sectionId}`
    });

    // Create a timeout promise (30 seconds)
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Request timeout: The server took too long to respond. Please try again.'));
      }, 30000); // 30 seconds timeout
    });

    // Wrap in queue mechanism
    this.executeCartOperation(async () => {
      try {
        const body = JSON.stringify({
          line: parseInt(line),
          quantity: parseInt(quantity),
          sections: `${this.dataset.sectionId}`
        });

        const response = await Promise.race([
          fetch(`${routes.cart_change_url}`, { ...Utility.fetchConfig(), ...{ body }}),
          timeoutPromise
        ]);

        if (!response.ok) {
          // Try to get error details from response body
          let errorMessage = response.statusText || 'An unknown error occurred.';
          let errorDetails = null;
          
          try {
            // Clone response to read body without consuming it
            const responseClone = response.clone();
            const errorText = await responseClone.text();
            console.error('[CART-DRAWER] updateItemQty - Error response body:', errorText);
            
            // Try to parse as JSON
            try {
              const errorJson = JSON.parse(errorText);
              errorDetails = errorJson;
              errorMessage = errorJson.description || errorJson.message || errorJson.errors || errorMessage;
              console.error('[CART-DRAWER] updateItemQty - Parsed error JSON:', errorJson);
            } catch (parseErr) {
              // If not JSON, use text if available
              if (errorText && errorText.trim()) {
                errorMessage = errorText;
              }
            }
          } catch (readError) {
            console.error('[CART-DRAWER] updateItemQty - Error reading response:', readError);
          }
          
          console.error('[CART-DRAWER] updateItemQty - Request failed:', {
            status: response.status,
            statusText: response.statusText,
            errorMessage,
            errorDetails
          });
          
          throw new Error(errorMessage);
        }

        const _state = await response.text();
        
        let dataJSON;
        try {
          dataJSON = ((typeof _state == 'object') ? _state : JSON.parse(_state));
        } catch (parseError) {
          console.error('[CART-DRAWER] updateItemQty - JSON parse error:', parseError);
          console.error('[CART-DRAWER] updateItemQty - Response text:', _state?.substring(0, 500));
          throw new Error('Invalid server response. Please try again.');
        }

        if(dataJSON.errors){
          console.error('[CART-DRAWER] updateItemQty - Errors in response:', dataJSON.errors);
          window.notificationEle.updateNotification('Cart Update', dataJSON.errors, {
            type: 'error',
            timeout: 5000
          });

          const cartItem = window.globalSpace.cart.items[line-1];
          if(qtyInput && cartItem) qtyInput.value = cartItem.quantity;
          throw new Error(dataJSON.errors);
        }

        eventBus.publish(PUB_SUB_EVENTS.cartUpdate, {
          data: {
            action: (quantity == 0) ? 'remove-item': 'update-qty',
            item: (quantity == 0) ? dataJSON.items_removed : dataJSON.items_added
          }
        });
        
        // Check if sections exist, if not fetch them separately
        if (dataJSON.sections && typeof dataJSON.sections === 'object' && Object.keys(dataJSON.sections).length > 0) {
          this._updateCart(dataJSON.sections);
        } else {
          console.warn('[CART-DRAWER] updateItemQty - Sections not in response or empty, fetching separately');
          const sectionId = this.dataset.sectionId || 'template-cart';
          const sectionsUrl = `${window.routes.cart_fetch_url}?sections=${sectionId}`;
          
          fetch(sectionsUrl)
            .then(res => {
              if (!res.ok) {
                throw new Error(`Failed to fetch sections: ${res.status}`);
              }
              return res.json();
            })
            .then(sectionsResponse => {
              // Handle Shopify sections API response format: { sections: { sectionId: html } }
              if (sectionsResponse && sectionsResponse.sections && sectionsResponse.sections[sectionId]) {
                this._updateCart(sectionsResponse.sections);
              } else if (sectionsResponse && sectionsResponse[sectionId]) {
                // Handle direct format: { sectionId: html }
                const sectionsObj = {};
                sectionsObj[sectionId] = sectionsResponse[sectionId];
                this._updateCart(sectionsObj);
              } else {
                console.error('[CART-DRAWER] updateItemQty - Could not find sections in fetched response');
              }
            })
            .catch(error => {
              console.error('[CART-DRAWER] updateItemQty - Error fetching sections:', error);
            });
        }
        
        return dataJSON;
      } catch (error) {
        // Handle all errors (timeout, fetch errors, network errors, etc.)
        console.error('[CART-DRAWER] updateItemQty - Error caught:', error);
        console.error('[CART-DRAWER] updateItemQty - Error type:', error.constructor.name);
        console.error('[CART-DRAWER] updateItemQty - Error message:', error.message);
        console.error('[CART-DRAWER] updateItemQty - Error stack:', error.stack);
        
        let errorMessage;
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else if (error && error.errors) {
          errorMessage = error.errors;
        } else {
          errorMessage = 'An error occurred while updating your cart. Please try again.';
        }
        
        // Show notification (will also be shown in processCartOperation, but this provides immediate feedback)
        if (window.notificationEle) {
          window.notificationEle.updateNotification('Cart Update Failed', errorMessage, {
            type: 'error',
            timeout: 5000
          });
        }

        // Revert quantity input on error
        const cartItem = window.globalSpace.cart.items[line-1];
        if(qtyInput && cartItem) qtyInput.value = cartItem.quantity;
        throw error;
      } finally {
        // Re-enable inputs
        if (qtyInput) {
          qtyInput.disabled = false;
        }
        this.cartForm.querySelectorAll('[data-item-remove]').forEach(btn => btn.disabled = false);
        
        setTimeout(() => {
          if(lineItem){ lineItem.classList.remove('updating'); }
        }, 500);
      }
    }).catch((error) => {
      // Queue rejection handler
      console.error('[CART-DRAWER] updateItemQty - Queue rejection:', error);
    });
  }

  /**
   * Remove Item Event
   */
  removeItem(event){
    event.preventDefault();
    
    // Prevent multiple clicks
    if (this.isCartUpdating) {
      return;
    }
    
    const {currentTarget} = event;
    const itemIndex = currentTarget.dataset.index || null;
    if(itemIndex !== null){
        this.updateItemQty(itemIndex, 0);
    }
  }

  /**
   * Cart Item Quantity Input change event
   * Added debouncing to prevent rapid-fire API calls
   */
  onQtyChange(evt){
    evt.preventDefault();
    
    // Prevent if cart is updating
    if (this.isCartUpdating) {
      const $qtyInput = evt.currentTarget;
      const itemIndex = $qtyInput.dataset.index || null;
      const cartItem = window.globalSpace.cart.items[itemIndex - 1];
      if (cartItem) {
        $qtyInput.value = cartItem.quantity;
      }
      return;
    }

    const $qtyInput = evt.currentTarget;
    const qtyValue = $qtyInput.value;
    const itemIndex = $qtyInput.dataset.index || null;

    if(itemIndex) {
      // Debounce rapid changes
      if (this._qtyChangeTimeout) {
        clearTimeout(this._qtyChangeTimeout);
      }
      
      this._qtyChangeTimeout = setTimeout(() => {
        this.updateItemQty(itemIndex, qtyValue);
      }, 300); // 300ms debounce
    }
  }
}
customElements.define("cart-form", AjaxCart);